import { AlertTriangle, CheckCircle2, Info, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";

export function Alert({
  children,
  className,
  title,
  variant = "info",
}: {
  children: ReactNode;
  className?: string;
  title: string;
  variant?: "info" | "success" | "warning" | "danger";
}) {
  const Icon =
    variant === "success"
      ? CheckCircle2
      : variant === "warning" || variant === "danger"
        ? AlertTriangle
        : Info;

  return (
    <div
      role="alert"
      className={cn(
        "flex gap-3 rounded-3xl border px-4 py-4",
        variant === "info" && "border-info/20 bg-info/8 text-info-foreground",
        variant === "success" &&
          "border-success/20 bg-success/10 text-success-foreground",
        variant === "warning" &&
          "border-warning/25 bg-warning/14 text-warning-foreground",
        variant === "danger" &&
          "border-destructive/20 bg-destructive/10 text-foreground",
        className,
      )}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="space-y-1">
        <p className="font-semibold">{title}</p>
        <div className="text-sm leading-6 text-current/85">{children}</div>
      </div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <LoaderCircle className={cn("h-5 w-5 animate-spin", className)} />;
}

export function Skeleton({
  className,
  rounded = "lg",
}: {
  className?: string;
  rounded?: "md" | "lg" | "full";
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse bg-muted",
        rounded === "md" && "rounded-md",
        rounded === "lg" && "rounded-xl",
        rounded === "full" && "rounded-full",
        className,
      )}
    />
  );
}

export function ProgressBar({
  className,
  value,
}: {
  className?: string;
  value: number;
}) {
  const clamped = Math.max(0, Math.min(100, value));

  return (
    <div
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={clamped}
      className={cn("h-2.5 overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function ProgressRing({
  className,
  label,
  size = 108,
  strokeWidth = 10,
  value,
}: {
  className?: string;
  label?: string;
  size?: number;
  strokeWidth?: number;
  value: number;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * 2 * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg
        aria-label={label ?? `Progress ${clamped}%`}
        className="-rotate-90"
        height={size}
        role="img"
        width={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          stroke="currentColor"
          strokeOpacity="0.12"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
        />
      </svg>
      <span className="absolute text-xl font-semibold">{clamped}%</span>
    </div>
  );
}
